import { useRouteState } from '../routeState'
import { ReferenceTextarea } from './ReferenceTextarea'
import { applyReference, renameReference } from '../connectedBible'
import { ReferenceNameInput } from './ReferenceNameInput'
import { WritingTaskControl } from './WritingTaskControl'
import { readBrowserValue, writeBrowserValue } from '../browserStorage'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
  AlignLeft,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Check,
  ChevronDown,
  FileText,
  Flag,
  Focus,
  GitBranch,
  MessageSquareText,
  Plus,
  X,
  Trash2,
} from 'lucide-react'
import {
  addPlayerChoice,
  createBlock,
  createNode,
  draftStatuses,
  nodeWordCount,
} from '../model'
import type { StoryBlock, StoryNode, StoryProject } from '../types'
import { ChoiceLogicEditor } from './StoryLogicEditor'

type ScreenplayEditorProps = {
  project: StoryProject
  selectedNodeId: string | null
  onSelectNode: (nodeId: string) => void
  onChange: (project: StoryProject, historyKey?: string) => void
  onAddNode: () => void
  focusMode: boolean
  onToggleFocus: () => void
  onOpenVariables: () => void
  onOpenReference: (id: string) => void
  onSaveStory: () => Promise<boolean>
}

export function ScreenplayEditor({
  project,
  selectedNodeId,
  onSelectNode,
  onChange,
  onAddNode,
  focusMode,
  onToggleFocus,
  onOpenVariables,
  onOpenReference,
  onSaveStory,
}: ScreenplayEditorProps) {
  const [fullDraft, setFullDraft] = useRouteState<boolean>('manuscript', false)
  const [typeface, setTypeface] = useState(() =>
    readBrowserValue('bramble:writing-typeface') === 'screenplay'
      ? 'screenplay'
      : 'literary',
  )
  const [textSize, setTextSize] = useState(() => {
    const size = Number(readBrowserValue('bramble:writing-size'))
    return [15, 17, 20].includes(size) ? size : 17
  })
  const activeBlock = useRef<{ nodeId: string; blockId: string } | null>(null)
  const pendingFocus = useRef<string | null>(null)
  const scroller = useRef<HTMLDivElement>(null)
  const selected =
    project.nodes.find((node) => node.id === selectedNodeId) ??
    project.nodes[0]
  const selectedIndex = project.nodes.indexOf(selected)
  const displayedNodes = fullDraft ? project.nodes : [selected]
  const speakers = Array.from(
    new Set([
      ...(project.bible?.characters.map((character) => character.name) ?? []),
      ...project.nodes.flatMap((node) =>
        node.blocks.map((block) => block.speaker ?? ''),
      ),
    ]),
  ).filter((name) => name && name !== 'CHARACTER')

  useEffect(() => {
    writeBrowserValue('bramble:writing-typeface', typeface)
    writeBrowserValue('bramble:writing-size', String(textSize))
  }, [typeface, textSize])

  useEffect(() => {
    if (fullDraft) {
      const article = document.querySelector(
        '[data-screenplay-node="' + CSS.escape(selected.id) + '"]',
      )
      if (!article?.contains(document.activeElement))
        article?.scrollIntoView({ block: 'start', behavior: 'smooth' })
    } else scroller.current?.scrollTo({ top: 0 })
  }, [selected.id, fullDraft])

  useLayoutEffect(() => {
    if (!pendingFocus.current) return
    document.getElementById(pendingFocus.current)?.focus()
    pendingFocus.current = null
  }, [project])

  const editNode = (
    node: StoryNode,
    changes: Partial<StoryNode>,
    key?: string,
  ) =>
    onChange(
      {
        ...project,
        nodes: project.nodes.map((item) =>
          item.id === node.id ? { ...item, ...changes } : item,
        ),
      },
      key,
    )

  const editBlock = (
    node: StoryNode,
    block: StoryBlock,
    changes: Partial<StoryBlock>,
    field: string,
  ) =>
    editNode(
      node,
      {
        blocks: node.blocks.map((item) =>
          item.id === block.id ? { ...item, ...changes } : item,
        ),
      },
      field + ':' + node.id + ':' + block.id,
    )

  const insertBlock = (
    kind: StoryBlock['kind'],
    node = selected,
    afterId?: string,
  ) => {
    const block = createBlock(kind)
    if (kind === 'dialogue') block.speaker = ''
    const after =
      afterId ??
      (activeBlock.current?.nodeId === node.id
        ? activeBlock.current.blockId
        : undefined)
    const index = after
      ? node.blocks.findIndex((item) => item.id === after) + 1
      : node.blocks.length
    const blocks = [...node.blocks]
    blocks.splice(index, 0, block)
    pendingFocus.current =
      (kind === 'dialogue' ? 'speaker-' : 'text-') + block.id
    editNode(node, { blocks }, 'insert-block:' + block.id)
  }

  const moveBlock = (node: StoryNode, index: number, direction: number) => {
    const blocks = [...node.blocks]
    const [block] = blocks.splice(index, 1)
    blocks.splice(index + direction, 0, block)
    editNode(node, { blocks }, 'reorder-block')
  }

  const addChoice = (node = selected) => {
    const next = addPlayerChoice(project, node.id)
    const choice = next.nodes
      .find((item) => item.id === node.id)
      ?.choices.at(-1)
    if (choice) pendingFocus.current = 'choice-' + choice.id
    onChange(next, 'add-choice')
  }

  const setRoute = (node: StoryNode, value: string, choiceId?: string) => {
    let next = project
    let targetId = value || null
    if (value === '__new__') {
      const created = createNode(project, {
        x: node.position.x + 390,
        y: node.position.y + node.choices.length * 100,
      })
      next = { ...project, nodes: [...project.nodes, created] }
      targetId = created.id
    }
    onChange(
      {
        ...next,
        nodes: next.nodes.map((item) =>
          item.id === node.id
            ? {
                ...item,
                ...(choiceId
                  ? {
                      choices: item.choices.map((choice) =>
                        choice.id === choiceId
                          ? { ...choice, targetId }
                          : choice,
                      ),
                    }
                  : { nextId: targetId }),
              }
            : item,
        ),
      },
      'set-route',
    )
  }

  const routeOptions = (currentId: string | null) => (
    <>
      <option value="">Choose a destination…</option>
      {currentId && !project.nodes.some((node) => node.id === currentId) && (
        <option value={currentId}>Missing scene</option>
      )}
      <option value="__new__">＋ Create a new scene</option>
      {project.nodes.map((node) => (
        <option key={node.id} value={node.id}>
          {node.title || 'Untitled scene'}
          {node.id === selected.id ? ' (loop back)' : ''}
        </option>
      ))}
    </>
  )

  return (
    <section
      className={
        'writer-editor' + (focusMode ? ' writer-editor--focused' : '')
      }
      aria-label="Screenplay editor"
    >
      <div
        className="writing-toolbar"
        role="toolbar"
        aria-label="Writing tools"
      >
        <div className="toolbar-group insert-tools">
          <button
            type="button"
            onClick={() => insertBlock('narration')}
            title="Insert action after the current block"
          >
            <AlignLeft size={16} />
            <span>Action</span>
          </button>
          <button
            type="button"
            onClick={() => insertBlock('dialogue')}
            title="Insert dialogue after the current block"
          >
            <MessageSquareText size={16} />
            <span>Dialogue</span>
          </button>
          <button
            type="button"
            onClick={() => addChoice()}
            disabled={selected.kind === 'ending'}
          >
            <GitBranch size={16} />
            <span>Choice</span>
          </button>
        </div>
        <div className="toolbar-group type-tools">
          <select
            aria-label="Writing typeface"
            value={typeface}
            onChange={(event) => setTypeface(event.target.value)}
          >
            <option value="literary">Literary</option>
            <option value="screenplay">Screenplay</option>
          </select>
          <select
            aria-label="Text size"
            value={textSize}
            onChange={(event) => setTextSize(Number(event.target.value))}
          >
            <option value={15}>15</option>
            <option value={17}>17</option>
            <option value={20}>20</option>
          </select>
        </div>
        <div className="toolbar-group document-tools">
          <button
            type="button"
            className={fullDraft ? 'is-active' : ''}
            aria-label={fullDraft ? 'Full draft' : 'This scene'}
            aria-pressed={fullDraft}
            onClick={() => setFullDraft(!fullDraft)}
            title="Switch between this scene and the full draft"
          >
            <FileText size={15} />
            <span>{fullDraft ? 'Full draft' : 'This scene'}</span>
            <ChevronDown size={12} />
          </button>
          <button
            type="button"
            className={focusMode ? 'is-active' : ''}
            aria-label={focusMode ? 'Exit focus' : 'Focus'}
            aria-pressed={focusMode}
            onClick={onToggleFocus}
            title="Focus mode · Cmd/Ctrl + Shift + F"
          >
            {focusMode ? <X size={16} /> : <Focus size={16} />}
            <span>{focusMode ? 'Exit focus' : 'Focus'}</span>
          </button>
        </div>
      </div>
      <div className="document-scroll" ref={scroller}>
        <div className="document-breadcrumb">
          <span>{project.title || 'Untitled story'}</span>
          <span>/</span>
          <span>
            {fullDraft ? 'Full draft' : selected.chapter || 'Manuscript'}
          </span>
          <span className="document-autosave">
            <Check size={12} /> Autosave on
          </span>
        </div>
        <div
          className={'manuscript manuscript--' + typeface}
          style={{ '--writing-size': textSize + 'px' } as React.CSSProperties}
        >
          {fullDraft && (
            <header className="manuscript-cover">
              <span>THE MANUSCRIPT</span>
              <h1>{project.title || 'Untitled story'}</h1>
              <p>{project.nodes.length} scenes · Working draft</p>
            </header>
          )}
          <datalist id="story-character-names">
            {speakers.map((speaker) => (
              <option key={speaker} value={speaker} />
            ))}
          </datalist>
          {displayedNodes.map((node) => {
            const index = project.nodes.indexOf(node)
            const status = node.status ?? 'draft'
            return (
              <article
                key={node.id}
                className="manuscript-scene"
                data-screenplay-node={node.id}
                onFocusCapture={() => onSelectNode(node.id)}
              >
                <header className="manuscript-heading">
                  <div className="manuscript-eyebrow">
                    <span>SCENE {String(index + 1).padStart(2, '0')}</span>
                    {node.id === project.startNodeId && (
                      <span className="scene-opening">
                        <Flag size={11} /> Opening scene
                      </span>
                    )}
                    <span className="draft-status" data-status={status}>
                      {
                        draftStatuses.find((item) => item.value === status)
                          ?.label
                      }
                    </span>
                  </div>
                  <ReferenceNameInput
                    className="manuscript-title"
                    data-live-path={JSON.stringify(['nodes', node.id, 'title'])}
                    id={'node-title-' + node.id}
                    aria-label={'Title for scene ' + (index + 1)}
                    value={node.title}
                    placeholder="Untitled scene"
                    onRename={(title) =>
                      onChange(
                        renameReference(project, 'scene:' + node.id, title),
                        'title:' + node.id,
                      )
                    }
                  />
                  <div className="manuscript-meta">
                    <span>
                      {node.kind === 'ending'
                        ? 'Ending'
                        : node.choices.length
                          ? 'Branching scene'
                          : 'Scene'}
                    </span>
                    <i /> <span>{nodeWordCount(node)} words</span>
                    {node.chapter && (
                      <>
                        <i />
                        <span>{node.chapter}</span>
                      </>
                    )}
                  </div>
                  <WritingTaskControl project={project} targetId={'scene:' + node.id} onSaveStory={onSaveStory} />
                </header>
                <div className="manuscript-body">
                  {node.blocks.length === 0 && (
                    <button
                      className="blank-page-prompt"
                      type="button"
                      onClick={() => insertBlock('narration', node)}
                    >
                      Every scene starts with a line. Start writing…
                    </button>
                  )}
                  {node.blocks.map((block, blockIndex) => (
                    <div
                      className={'writing-block writing-block--' + block.kind}
                      key={block.id}
                      onFocusCapture={() => {
                        activeBlock.current = {
                          nodeId: node.id,
                          blockId: block.id,
                        }
                      }}
                    >
                      <div className="writing-block-tools">
                        <select
                          aria-label={
                            'Block type for line ' + (blockIndex + 1)
                          }
                          value={block.kind}
                          onChange={(event) =>
                            editBlock(
                              node,
                              block,
                              {
                                kind: event.target
                                  .value as StoryBlock['kind'],
                              },
                              'block-kind',
                            )
                          }
                        >
                          <option value="narration">Action</option>
                          <option value="dialogue">Dialogue</option>
                        </select>
                        <button
                          type="button"
                          aria-label="Move line up"
                          disabled={blockIndex === 0}
                          onClick={() => moveBlock(node, blockIndex, -1)}
                        >
                          <ArrowUp size={13} />
                        </button>
                        <button
                          type="button"
                          aria-label="Move line down"
                          disabled={blockIndex === node.blocks.length - 1}
                          onClick={() => moveBlock(node, blockIndex, 1)}
                        >
                          <ArrowDown size={13} />
                        </button>
                        <button
                          type="button"
                          aria-label="Remove line"
                          onClick={() =>
                            editNode(
                              node,
                              {
                                blocks: node.blocks.filter(
                                  (item) => item.id !== block.id,
                                ),
                              },
                              'remove-block',
                            )
                          }
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                      {block.kind === 'dialogue' && (
                        <div className="writing-cue">
                          <input
                            data-live-path={JSON.stringify(['nodes', node.id, 'blocks', block.id, 'speaker'])}
                        id={'speaker-' + block.id}
                            aria-label="Character name"
                            list="story-character-names"
                            value={block.speaker ?? ''}
                            onChange={(event) =>
                              editBlock(
                                node,
                                block,
                                { speaker: event.target.value },
                                'speaker',
                              )
                            }
                            placeholder="CHARACTER"
                          />
                          <input
                            data-live-path={JSON.stringify(['nodes', node.id, 'blocks', block.id, 'direction'])}
                        id={'direction-' + block.id}
                            aria-label="Dialogue direction"
                            value={block.direction ?? ''}
                            onChange={(event) =>
                              editBlock(
                                node,
                                block,
                                { direction: event.target.value },
                                'direction',
                              )
                            }
                            placeholder="(a beat, a whisper, a look…)"
                          />
                        </div>
                      )}
                      <ReferenceTextarea
                        project={project}
                        sourceId={'scene:' + node.id}
                        grow
                        onOpenReference={onOpenReference}
                        data-live-path={JSON.stringify(['nodes', node.id, 'blocks', block.id, 'text'])}
                        id={'text-' + block.id}
                        aria-label={
                          block.kind === 'dialogue'
                            ? 'Dialogue text'
                            : 'Action text'
                        }
                        className={
                          'writing-text writing-text--' +
                          textSize +
                          '-' +
                          typeface
                        }
                        value={block.text}
                        spellCheck
                        onText={(text, pick) =>
                          onChange(
                            applyReference(
                              {
                                ...project,
                                nodes: project.nodes.map((n) =>
                                  n.id === node.id
                                    ? {
                                        ...n,
                                        blocks: n.blocks.map((b) =>
                                          b.id === block.id
                                            ? { ...b, text }
                                            : b,
                                        ),
                                      }
                                    : n,
                                ),
                              },
                              'scene:' + node.id,
                              pick,
                            ),
                            'block:' + block.id + ':text',
                          )
                        }
                        onKeyDown={(event) => {
                          if (
                            (event.metaKey || event.ctrlKey) &&
                            event.key === 'Enter'
                          ) {
                            event.preventDefault()
                            insertBlock(
                              event.shiftKey ? 'dialogue' : block.kind,
                              node,
                              block.id,
                            )
                          }
                        }}
                        placeholder={
                          block.kind === 'dialogue'
                            ? 'What do they say?'
                            : 'Set the scene. What does the player see, hear, or feel?'
                        }
                        rows={1}
                      />
                    </div>
                  ))}
                  <div className="manuscript-insert">
                    <span />
                    <button
                      type="button"
                      onClick={() =>
                        insertBlock('narration', node, node.blocks.at(-1)?.id)
                      }
                    >
                      <Plus size={13} /> Action
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        insertBlock('dialogue', node, node.blocks.at(-1)?.id)
                      }
                    >
                      <Plus size={13} /> Dialogue
                    </button>
                    <span />
                  </div>
                </div>
                {node.kind === 'ending' ? (
                  <div className="manuscript-ending">
                    <Flag size={16} />
                    <span>The story ends here</span>
                    <small>An ending the player can reach.</small>
                  </div>
                ) : (
                  <section
                    className="story-routes"
                    aria-label={'Branches from ' + node.title}
                  >
                    <header>
                      <div>
                        <GitBranch size={17} />
                        <strong>
                          {node.choices.length
                            ? 'The player decides'
                            : 'What happens next?'}
                        </strong>
                      </div>
                      <span>
                        {node.choices.length
                          ? node.choices.length + ' choices'
                          : 'Story flow'}
                      </span>
                    </header>
                    {node.choices.map((choice, choiceIndex) => (
                      <div className="story-choice" key={choice.id}>
                        <span className="choice-number">
                          {String(choiceIndex + 1).padStart(2, '0')}
                        </span>
                        <div className="choice-fields">
                          <ReferenceTextarea
                            project={project}
                            sourceId={'scene:' + node.id}
                            grow
                            rows={1}
                            onOpenReference={onOpenReference}
                            data-live-path={JSON.stringify(['nodes', node.id, 'choices', choice.id, 'label'])}
                            id={'choice-' + choice.id}
                            aria-label={'Choice ' + (choiceIndex + 1)}
                            value={choice.label}
                            placeholder="What can the player choose?"
                            onText={(label, pick) =>
                              onChange(
                                applyReference(
                                  {
                                    ...project,
                                    nodes: project.nodes.map((item) =>
                                      item.id === node.id
                                        ? {
                                            ...item,
                                            choices: item.choices.map(
                                              (choiceItem) =>
                                                choiceItem.id === choice.id
                                                  ? { ...choiceItem, label }
                                                  : choiceItem,
                                            ),
                                          }
                                        : item,
                                    ),
                                  },
                                  'scene:' + node.id,
                                  pick,
                                ),
                                'choice-label:' + choice.id,
                              )
                            }
                          />
                          <div className="choice-destination">
                            <ArrowRight size={13} />
                            <select
                              aria-label={
                                'Destination for choice ' + (choiceIndex + 1)
                              }
                              value={choice.targetId ?? ''}
                              data-unlinked={!choice.targetId}
                              onChange={(event) =>
                                setRoute(node, event.target.value, choice.id)
                              }
                            >
                              {routeOptions(choice.targetId)}
                            </select>
                            {choice.targetId &&
                              project.nodes.some(
                                (candidate) =>
                                  candidate.id === choice.targetId,
                              ) && (
                                <button
                                  type="button"
                                  title="Open destination scene"
                                  aria-label={
                                    'Open destination for choice ' +
                                    (choiceIndex + 1)
                                  }
                                  onClick={() =>
                                    onSelectNode(choice.targetId!)
                                  }
                                >
                                  <ArrowRight size={14} />
                                </button>
                              )}
                          </div>
                          <ChoiceLogicEditor
                            project={project}
                            choice={choice}
                            choiceNumber={choiceIndex + 1}
                            onOpenVariables={onOpenVariables}
                            onChange={(changes) =>
                              editNode(
                                node,
                                {
                                  choices: node.choices.map((item) =>
                                    item.id === choice.id
                                      ? { ...item, ...changes }
                                      : item,
                                  ),
                                },
                                'choice-logic:' + choice.id,
                              )
                            }
                          />
                        </div>
                        <button
                          type="button"
                          className="icon-button remove-choice"
                          aria-label={'Remove choice ' + (choiceIndex + 1)}
                          onClick={() =>
                            editNode(
                              node,
                              {
                                choices: node.choices.filter(
                                  (item) => item.id !== choice.id,
                                ),
                              },
                              'remove-choice',
                            )
                          }
                        >
                          <X size={15} />
                        </button>
                      </div>
                    ))}
                    {!node.choices.length && (
                      <div className="scene-continuation">
                        <ArrowRight size={15} />
                        <select
                          aria-label="Continue to scene"
                          value={node.nextId ?? ''}
                          onChange={(event) =>
                            setRoute(node, event.target.value)
                          }
                        >
                          {routeOptions(node.nextId)}
                        </select>
                        {node.nextId &&
                          project.nodes.some(
                            (candidate) => candidate.id === node.nextId,
                          ) && (
                            <button
                              type="button"
                              onClick={() => onSelectNode(node.nextId!)}
                            >
                              Open <ArrowRight size={13} />
                            </button>
                          )}
                      </div>
                    )}
                    <button
                      className="route-add-choice"
                      type="button"
                      onClick={() => addChoice(node)}
                    >
                      <Plus size={14} /> Add player choice
                    </button>
                  </section>
                )}
                <footer className="manuscript-scene-footer">
                  <span>{nodeWordCount(node)} words</span>
                  <span>
                    Scene {index + 1} of {project.nodes.length}
                  </span>
                </footer>
              </article>
            )
          })}
        </div>
        <div className="manuscript-navigation">
          <button
            type="button"
            disabled={selectedIndex === 0}
            onClick={() => onSelectNode(project.nodes[selectedIndex - 1].id)}
          >
            <ArrowLeft size={14} /> Previous scene
          </button>
          {selectedIndex < project.nodes.length - 1 ? (
            <button
              type="button"
              onClick={() =>
                onSelectNode(project.nodes[selectedIndex + 1].id)
              }
            >
              Next scene <ArrowRight size={14} />
            </button>
          ) : (
            <button type="button" onClick={onAddNode}>
              New scene <Plus size={14} />
            </button>
          )}
        </div>
        <p className="writing-shortcut-hint">
          <kbd>⌘ / Ctrl</kbd> + <kbd>Enter</kbd> new block <span>·</span> Add{' '}
          <kbd>Shift</kbd> for dialogue
        </p>
      </div>
    </section>
  )
}
